import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Database, Loader2, Search, AlertTriangle, ArrowLeft, Sparkles, Send, CheckCircle2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '@/components/ui/Button';

/**
 * CoreLookupPage — live lookups against the core banking sandbox (Jack Henry Symitar via Uptiq).
 *
 * This is the first screen in the CRM fed by something other than seed data. It calls the
 * read-only endpoints in crm-api's CoreBankingModule:
 *   GET /api/core/sandbox/members?lastName=&firstName=
 *   GET /api/core/sandbox/members/:memberNumber
 * Each call is brokered by an Uptiq agent that executes fixed SQL against the sandbox, so
 * expect several seconds per request. Failures render as failures — never as "no data".
 *
 * Values arrive as strings ('' means null) because the transport casts every column to text.
 */

type Row = Record<string, string>;

interface MemberBundle {
  member: Row | null;
  accounts: Row[];
  loans: Row[];
  recentTransactions: Row[];
  meta: SandboxMeta[];
}

/** Mirrors the API's SandboxQueryMeta. `source` is 'local' when CORE_SANDBOX_SOURCE=local (synthetic replica). */
interface SandboxMeta { executionId: string; latencyMs: number; sql: string; source?: 'uptiq' | 'local' }

type CoreSource = 'uptiq' | 'local' | null;

function sourceOf(meta: SandboxMeta | SandboxMeta[] | undefined): CoreSource {
  const m = Array.isArray(meta) ? meta[0] : meta;
  return m?.source ?? (m ? 'uptiq' : null);
}

function getJwt(): string {
  return localStorage.getItem('crm_dev_token') ?? '';
}

/** Error with the API's machine-readable code (e.g. insights_ungrounded) so the UI can pick a distinct state. */
export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function api<T>(path: string, init?: { method?: 'GET' | 'POST'; body?: unknown }): Promise<T> {
  const res = await fetch(path, {
    method: init?.method ?? 'GET',
    headers: { Authorization: `Bearer ${getJwt()}`, ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}) },
    ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  if (res.status === 503) throw new ApiError('The core sandbox is not configured on this environment.', 503);
  if (res.status === 504) throw new ApiError('The agent timed out. Try again.', 504);
  if (res.status === 401) throw new ApiError('Your session has expired. Sign in again.', 401);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    const code = typeof body.error === 'string' && /^[a-z_]+$/.test(body.error) ? body.error : undefined;
    throw new ApiError(body.message ?? `Request failed (HTTP ${res.status}).`, res.status, code);
  }
  return (await res.json()) as T;
}

function money(v: string | undefined): string {
  if (v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function date(v: string | undefined): string {
  if (v === undefined || v === '') return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function text(v: string | undefined): string {
  return v === undefined || v === '' ? '—' : v;
}

function SandboxBadge({ source }: { source: CoreSource }): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-info-soft text-info text-xs px-2 py-0.5 font-medium">
      <Database className="h-3 w-3" aria-hidden="true" />
      {source === 'local' ? 'Synthetic core sandbox · local' : source === 'uptiq' ? 'Symitar sandbox · via Uptiq' : 'Core banking sandbox'}
    </span>
  );
}

function ErrorBox({ message }: { message: string }): React.ReactElement {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-lg border border-fail-soft bg-fail-soft px-4 py-3 text-sm text-fail">
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

function Spinner({ label }: { label: string }): React.ReactElement {
  return (
    <div className="flex items-center gap-2 text-sm text-ink-muted py-6 justify-center" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {label}
    </div>
  );
}

function Table({ columns, rows, empty }: { columns: { key: string; label: string; render?: (r: Row) => string; right?: boolean }[]; rows: Row[]; empty: string }): React.ReactElement {
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="min-w-full text-sm">
        <thead className="bg-bg text-xs uppercase tracking-wide text-ink-muted">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={cn('px-3 py-2 font-medium', c.right ? 'text-right' : 'text-left')}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-sunken bg-white">
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-ink-muted">{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="hover:bg-bg">
              {columns.map((c) => (
                <td key={c.key} className={cn('px-3 py-2 text-ink-soft whitespace-nowrap', c.right ? 'text-right tabular-nums' : '')}>
                  {c.render ? c.render(r) : text(r[c.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- Member Intelligence (Uptiq agent → grounding checker → staff review queue) ----

interface Evidence { table: string; key: string; value: string }
interface Insight {
  kind: string;
  headline: string;
  evidence: Evidence[];
  suggested_action: string;
  confidence: number;
}
interface MemberIntelResult {
  insights: Insight[];
  dropped_ungrounded: { insight: Insight; reasons: string[] }[];
  data_gaps: string[];
  meta: { agentLatencyMs: number; totalLatencyMs: number; executionId: string };
}

const KIND_LABEL: Record<string, string> = {
  cd_maturity: 'CD maturing',
  refi_opportunity: 'Refi opportunity',
  idle_deposits: 'Idle deposits',
  direct_deposit_stopped: 'Direct deposit stopped',
  card_inactive: 'Card inactive',
  other: 'Other',
};

function insightsErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return (err as Error).message;
  if (err.code === 'insights_ungrounded') return 'The agent cited facts that are not in this member’s core data, so nothing was shown. Try again; if it repeats, the agent instructions need work.';
  if (err.code === 'insights_schema') return 'The agent replied in an unexpected shape. Nothing was shown. Try again.';
  if (err.status === 404) return 'This member was not found in the core.';
  if (err.status === 502) return `The agent call failed: ${err.message}`;
  return err.message;
}

function InsightCard({ insight, selected, onToggle }: { insight: Insight; selected: boolean; onToggle: () => void }): React.ReactElement {
  const pct = Math.round(insight.confidence * 100);
  return (
    <label className={cn('block rounded-xl border bg-white p-4 cursor-pointer transition-colors', selected ? 'border-brand ring-1 ring-brand/30' : 'border-line hover:border-line-strong')}>
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={selected} onChange={onToggle} className="mt-1 h-4 w-4 accent-brand" aria-label={`Select insight: ${insight.headline}`} />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex rounded-full bg-warn-soft text-warn text-[11px] font-medium px-2 py-0.5">{KIND_LABEL[insight.kind] ?? insight.kind}</span>
            <span className="text-[11px] text-ink-muted" title="Agent confidence">confidence {pct}%</span>
            <span className="h-1.5 w-20 rounded-full bg-sunken overflow-hidden" aria-hidden="true"><span className="block h-full bg-brand" style={{ width: `${pct}%` }} /></span>
          </div>
          <p className="text-sm font-medium text-ink">{insight.headline}</p>
          <div className="flex flex-wrap gap-1.5">
            {insight.evidence.map((e, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-md border border-pass-soft bg-pass-soft text-pass text-[11px] px-1.5 py-0.5 font-mono" title={`${e.table} · ${e.key}`}>
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                {e.table}.{e.key.split(';').pop()?.replace('field=', '') ?? e.key} = {e.value === '' ? '∅' : e.value}
              </span>
            ))}
          </div>
          <p className="text-sm text-ink-soft"><span className="text-xs uppercase tracking-wide text-ink-muted mr-1">Next step</span>{insight.suggested_action}</p>
        </div>
      </div>
    </label>
  );
}

function InsightsPanel({ memberNumber }: { memberNumber: string }): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MemberIntelResult | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [retrying, setRetrying] = useState(false);
  const generate = useCallback(async () => {
    setLoading(true); setError(null); setResult(null); setPicked(new Set()); setSendError(null); setRetrying(false);
    try {
      const path = `/api/core/sandbox/members/${encodeURIComponent(memberNumber)}/insights`;
      let data: MemberIntelResult;
      try {
        data = await api<MemberIntelResult>(path, { method: 'POST' });
      } catch (first) {
        // The executor hangs single calls intermittently; the core queries are cached server-side, so a
        // second attempt costs only the agent call. One retry, visibly — never a silent loop.
        if (!(first instanceof ApiError && first.status === 504)) throw first;
        setRetrying(true);
        data = await api<MemberIntelResult>(path, { method: 'POST' });
      }
      setResult(data);
      setPicked(new Set(data.insights.map((_, i) => i)));
    } catch (err) {
      setError(insightsErrorMessage(err));
    } finally {
      setLoading(false); setRetrying(false);
    }
  }, [memberNumber]);

  const send = useCallback(async () => {
    if (!result || picked.size === 0) return;
    setSending(true); setSendError(null);
    try {
      const insights = result.insights.filter((_, i) => picked.has(i));
      const r = await api<{ itemIds: string[] }>(`/api/core/sandbox/members/${encodeURIComponent(memberNumber)}/insights/queue`, { method: 'POST', body: { insights } });
      setToast(`${r.itemIds.length} insight${r.itemIds.length === 1 ? '' : 's'} sent to the review queue. Staff approval is required before anything reaches the member.`);
      setPicked(new Set());
      window.setTimeout(() => setToast(null), 6000);
    } catch (err) {
      setSendError(insightsErrorMessage(err));
    } finally {
      setSending(false);
    }
  }, [result, picked, memberNumber]);

  return (
    <section className="rounded-xl border border-line bg-white p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-warn" aria-hidden="true" /> Member Intelligence
          </h3>
          <p className="text-xs text-ink-muted mt-0.5">Asks the Member Intelligence agent what to talk about. Every cited fact is re-checked against the core before it is shown; nothing goes to the member without staff approval.</p>
        </div>
        <Button size="sm" type="button" onClick={() => { void generate(); }} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Sparkles className="h-4 w-4" aria-hidden="true" />}
          {loading ? (retrying ? 'Agent timed out once — retrying…' : 'Generating…') : result ? 'Regenerate insights' : 'Generate insights'}
        </Button>
      </div>

      {loading && <Spinner label="Asking the Member Intelligence agent… (bundle + agent call, typically 15–40s)" />}
      {error && <ErrorBox message={error} />}

      {result && (
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            {result.insights.map((ins, i) => (
              <InsightCard key={i} insight={ins} selected={picked.has(i)} onToggle={() => setPicked((p) => { const n = new Set(p); if (n.has(i)) n.delete(i); else n.add(i); return n; })} />
            ))}
          </div>

          {result.dropped_ungrounded.length > 0 && (
            <details className="rounded-lg border border-brand bg-brand-tint px-4 py-2 text-xs text-brand-dark">
              <summary className="cursor-pointer font-medium">{result.dropped_ungrounded.length} insight{result.dropped_ungrounded.length === 1 ? '' : 's'} hidden — cited facts not found in the core</summary>
              <ul className="mt-2 space-y-1 list-disc pl-4">
                {result.dropped_ungrounded.map((d, i) => <li key={i}><span className="font-medium">{d.insight.headline}</span> — {d.reasons.join('; ')}</li>)}
              </ul>
            </details>
          )}
          {result.data_gaps.length > 0 && (
            <p className="text-xs text-ink-muted"><span className="font-medium">Data gaps:</span> {result.data_gaps.join(' · ')}</p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <span className="text-xs text-ink-muted">agent {(result.meta.agentLatencyMs / 1000).toFixed(1)}s · total {(result.meta.totalLatencyMs / 1000).toFixed(1)}s · {result.meta.executionId}</span>
            <button type="button" onClick={() => { void send(); }} disabled={sending || picked.size === 0} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-brand text-brand-dark hover:bg-info-soft disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
              Send {picked.size > 0 ? `${picked.size} ` : ''}to review queue
            </button>
          </div>
          {sendError && <ErrorBox message={sendError} />}
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-6 right-6 z-50 flex items-start gap-2 rounded-lg bg-ink text-white text-sm px-4 py-3 shadow-lg max-w-sm">
          <CheckCircle2 className="h-4 w-4 mt-0.5 text-pass flex-shrink-0" aria-hidden="true" />
          <span>{toast}</span>
          <button type="button" onClick={() => setToast(null)} className="ml-2 text-white/70 hover:text-white" aria-label="Dismiss">×</button>
        </div>
      )}
    </section>
  );
}

export function CoreLookupPage(): React.ReactElement {
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<Row[] | null>(null);

  const [selected, setSelected] = useState<string | null>(null);
  const [loadingBundle, setLoadingBundle] = useState(false);
  const [bundleError, setBundleError] = useState<string | null>(null);
  const [bundle, setBundle] = useState<MemberBundle | null>(null);
  const [coreSource, setCoreSource] = useState<CoreSource>(null);
  // Progressive fill: memberBundle renders with the first 5 transactions (its slowest single query,
  // and the one most worth shrinking); the next 10 arrive silently afterward and get appended. This
  // ref — not state — guards against a stale backfill landing after the user has opened someone else.
  const [moreTxnLoading, setMoreTxnLoading] = useState(false);
  const openMemberRef = useRef<string | null>(null);
  const FIRST_PAGE_SIZE = 5;
  const BACKFILL_PAGE_SIZE = 10;

  const search = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (lastName.trim().length === 0) { setSearchError('Enter a last name.'); return; }
    setSearching(true); setSearchError(null); setResults(null); setSelected(null); setBundle(null);
    try {
      const q = new URLSearchParams({ lastName: lastName.trim(), limit: '25' });
      if (firstName.trim()) q.set('firstName', firstName.trim());
      const data = await api<{ members: Row[]; meta?: SandboxMeta }>(`/api/core/sandbox/members?${q.toString()}`);
      setResults(data.members);
      setCoreSource(sourceOf(data.meta));
    } catch (err) {
      setSearchError((err as Error).message);
    } finally {
      setSearching(false);
    }
  }, [lastName, firstName]);

  const open = useCallback(async (memberNumber: string) => {
    openMemberRef.current = memberNumber;
    setSelected(memberNumber); setLoadingBundle(true); setBundleError(null); setBundle(null); setMoreTxnLoading(false);
    try {
      const data = await api<MemberBundle>(`/api/core/sandbox/members/${encodeURIComponent(memberNumber)}?txnLimit=${FIRST_PAGE_SIZE}`);
      setBundle(data);
      setCoreSource(sourceOf(data.meta));
      if (data.member !== null) {
        setMoreTxnLoading(true);
        api<{ transactions: Row[] }>(
          `/api/core/sandbox/members/${encodeURIComponent(memberNumber)}/transactions?offset=${FIRST_PAGE_SIZE}&limit=${BACKFILL_PAGE_SIZE}`,
        )
          .then((more) => {
            if (openMemberRef.current !== memberNumber) return; // the user moved on — drop this backfill
            setBundle((prev) => (prev ? { ...prev, recentTransactions: [...prev.recentTransactions, ...more.transactions] } : prev));
          })
          .catch(() => { /* the first page already rendered; a backfill miss just means fewer than 15 rows */ })
          .finally(() => { if (openMemberRef.current === memberNumber) setMoreTxnLoading(false); });
      }
    } catch (err) {
      setBundleError((err as Error).message);
    } finally {
      setLoadingBundle(false);
    }
  }, []);

  // Deep link from the Members page: /core-lookup?member=<memberNumber> opens the bundle directly.
  const [searchParams] = useSearchParams();
  const linkedMember = searchParams.get('member');
  useEffect(() => {
    if (linkedMember !== null && /^\d+$/.test(linkedMember) && selected === null && !loadingBundle) void open(linkedMember);
  }, [linkedMember]);

  const totalLatency = bundle?.meta.reduce((s, m) => s + m.latencyMs, 0) ?? 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink flex items-center gap-2">
            <Database className="h-5 w-5 text-brand-dark" aria-hidden="true" />
            Core Lookup
          </h1>
          <p className="text-sm text-ink-muted mt-1">
            Live, read-only lookups against the core banking sandbox. Each query is brokered by an agent and takes a few seconds.
          </p>
        </div>
        <SandboxBadge source={coreSource} />
      </div>

      {selected === null ? (
        <>
          <form onSubmit={(e) => { void search(e); }} className="flex flex-wrap items-end gap-3 rounded-xl border border-line bg-white p-4">
            <label className="flex flex-col text-xs font-medium text-ink-soft gap-1">
              Last name
              <input value={lastName} onChange={(e) => setLastName(e.target.value as string)} placeholder="Johnson" className="px-3 py-2 text-sm border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-info w-56" />
            </label>
            <label className="flex flex-col text-xs font-medium text-ink-soft gap-1">
              First name (optional)
              <input value={firstName} onChange={(e) => setFirstName(e.target.value as string)} placeholder="Mark" className="px-3 py-2 text-sm border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-info w-56" />
            </label>
            <Button size="sm" type="submit" disabled={searching}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Search className="h-4 w-4" aria-hidden="true" />}
              {searching ? 'Querying core…' : 'Search core'}
            </Button>
          </form>

          {searchError && <ErrorBox message={searchError} />}
          {searching && <Spinner label="Asking the core sandbox…" />}
          {results !== null && !searching && (
            <div className="space-y-2">
              <p className="text-xs text-ink-muted">{results.length} member{results.length === 1 ? '' : 's'} returned by the core</p>
              <Table
                empty="No members matched in the core."
                rows={results}
                columns={[
                  { key: 'member_number', label: 'Member #' },
                  { key: 'name', label: 'Name', render: (r) => `${text(r['first_name'])} ${text(r['last_name'])}` },
                  { key: 'member_status', label: 'Status' },
                  { key: 'member_type', label: 'Type' },
                  { key: 'risk_tier', label: 'Risk tier' },
                  { key: 'join_date', label: 'Joined', render: (r) => date(r['join_date']) },
                  { key: 'ssn_last4', label: 'SSN', render: (r) => (r['ssn_last4'] ? `•••-••-${r['ssn_last4']}` : '—') },
                  { key: 'open', label: '', render: () => 'Open →' },
                ]}
              />
              {results.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {results.map((r) => (
                    <button key={r['member_number']} onClick={() => { void open(r['member_number'] ?? ''); }} className="text-xs px-3 py-1.5 rounded-lg border border-line-strong hover:bg-bg text-ink-soft">
                      Open #{r['member_number']} · {text(r['first_name'])} {text(r['last_name'])}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-5">
          <button onClick={() => { setSelected(null); setBundle(null); setBundleError(null); }} className="inline-flex items-center gap-1 text-sm text-brand-dark hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to results
          </button>

          {loadingBundle && <Spinner label={`Pulling member #${selected} from the core — accounts, loans and transactions run in parallel…`} />}
          {bundleError && <ErrorBox message={bundleError} />}

          {bundle && bundle.member === null && (
            <ErrorBox message={`Member #${selected} was not found in the core.`} />
          )}

          {bundle && bundle.member && (
            <>
              <section className="rounded-xl border border-line bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-ink">
                      {text(bundle.member['first_name'])} {text(bundle.member['last_name'])}
                      <span className="ml-2 text-sm font-normal text-ink-muted">#{bundle.member['member_number']}</span>
                    </h2>
                    <p className="text-sm text-ink-muted mt-0.5">
                      {text(bundle.member['member_status'])} · {text(bundle.member['member_type'])} · joined {date(bundle.member['join_date'])} · risk tier {text(bundle.member['risk_tier'])}
                    </p>
                  </div>
                  <div className="text-right text-xs text-ink-muted">
                    <SandboxBadge source={coreSource} />
                    <div className="mt-1">{bundle.meta.length} {coreSource === 'local' ? 'queries' : 'agent calls'} · {(totalLatency / 1000).toFixed(1)}s</div>
                  </div>
                </div>
                <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {[
                    ['SSN', bundle.member['ssn_last4'] ? `•••-••-${bundle.member['ssn_last4']}` : '—'],
                    ['Occupation', text(bundle.member['occupation'])],
                    ['Employer', text(bundle.member['employer'])],
                    ['Est. income', money(bundle.member['estimated_annual_income'])],
                    ['Branch', text(bundle.member['preferred_branch_id'])],
                    ['Contact pref.', text(bundle.member['preferred_contact_method'])],
                    ['OFAC', text(bundle.member['ofac_status'])],
                    ['CDD risk', text(bundle.member['cdd_risk_rating'])],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs text-ink-muted">{k}</dt>
                      <dd className="text-ink">{v}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <InsightsPanel memberNumber={selected} />

              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-ink">Accounts ({bundle.accounts.length})</h3>
                <Table
                  empty="No accounts in the core."
                  rows={bundle.accounts}
                  columns={[
                    { key: 'account_number', label: 'Account #' },
                    { key: 'account_type', label: 'Type' },
                    { key: 'account_subtype', label: 'Subtype' },
                    { key: 'product_code', label: 'Product' },
                    { key: 'role', label: 'Role', render: (r) => `${text(r['role'])}${r['ownership_pct'] ? ` · ${Number(r['ownership_pct']).toFixed(0)}%` : ''}` },
                    { key: 'status_code', label: 'Status' },
                    { key: 'open_date', label: 'Opened', render: (r) => date(r['open_date']) },
                    { key: 'current_balance', label: 'Balance', right: true, render: (r) => money(r['current_balance']) },
                    { key: 'available_balance', label: 'Available', right: true, render: (r) => money(r['available_balance']) },
                  ]}
                />
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-ink">Loans ({bundle.loans.length})</h3>
                <Table
                  empty="No loans in the core."
                  rows={bundle.loans}
                  columns={[
                    { key: 'account_number', label: 'Account #' },
                    { key: 'loan_type', label: 'Type' },
                    { key: 'loan_program', label: 'Program' },
                    { key: 'delinquency_status', label: 'Delinquency' },
                    { key: 'days_past_due', label: 'DPD', right: true },
                    { key: 'interest_rate_apr', label: 'APR', right: true, render: (r) => (r['interest_rate_apr'] ? `${Number(r['interest_rate_apr']).toFixed(2)}%` : '—') },
                    { key: 'original_principal', label: 'Original', right: true, render: (r) => money(r['original_principal']) },
                    { key: 'current_principal', label: 'Current', right: true, render: (r) => money(r['current_principal']) },
                    { key: 'next_payment_date', label: 'Next payment', render: (r) => `${date(r['next_payment_date'])} · ${money(r['next_payment_amount'])}` },
                    { key: 'maturity_date', label: 'Maturity', render: (r) => date(r['maturity_date']) },
                  ]}
                />
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-ink">
                  Recent transactions ({bundle.recentTransactions.length})
                  {moreTxnLoading && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-ink-muted align-middle">
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                      loading more…
                    </span>
                  )}
                </h3>
                <Table
                  empty="No transactions in the core."
                  rows={bundle.recentTransactions}
                  columns={[
                    { key: 'posted_at', label: 'Posted', render: (r) => date(r['posted_at']) },
                    { key: 'account_number', label: 'Account #' },
                    { key: 'txn_category', label: 'Category' },
                    { key: 'description', label: 'Description' },
                    { key: 'channel', label: 'Channel' },
                    { key: 'counterparty_name', label: 'Counterparty' },
                    { key: 'amount', label: 'Amount', right: true, render: (r) => money(r['amount']) },
                    { key: 'running_balance', label: 'Balance', right: true, render: (r) => money(r['running_balance']) },
                  ]}
                />
              </section>
            </>
          )}
        </div>
      )}
    </div>
  );
}
